import { useState, useEffect } from 'react';
import { Plus, EyeOff } from 'lucide-react';
import { palette, shadow, transition, admin } from '../../styles/tokens';
import { useAuthStore } from '../../stores/authStore';
import { useIndustryLens } from '../../hooks/useIndustryLens';
import { useProjects } from './hooks';
import type { ProjectRow } from './hooks';
import { ActiveTab } from './ActiveTab';
import { CompletedTab } from './CompletedTab';
import { CreateProjectPanel } from './CreateProjectPanel';
import { ProjectDetailPanel } from './ProjectDetailPanel';
import { Skeleton, TableSkeleton } from '../../components/common/Skeleton';
import { Toast } from '../../components/common/Toast';
import { PROJECT_TABS, type ProjectTab } from './config';

export function ProjectsPage() {
  const business = useAuthStore(s => s.business);
  const user = useAuthStore(s => s.user);
  const [tab, setTab] = useState<ProjectTab>('active');
  const [showCreate, setShowCreate] = useState(false);
  const [selected, setSelected] = useState<ProjectRow | null>(null);
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);

  const { features, isLensActive, industryName } = useIndustryLens();
  const { projects, allProjects, users, loading, error, summary, filters, setFilters, createProject, updateStatus } = useProjects(business?.id);

  useEffect(() => { document.title = 'CrewBit · Projects'; }, []);

  const handleAdvance = async (id: string, status: string) => {
    if (!user) return;
    const ok = await updateStatus(id, status, user.id);
    if (ok) setToast({ msg: `Project marked ${status}`, type: 'success' });
  };

  const handleHold = async (id: string, reason: string) => {
    if (!user) return;
    const ok = await updateStatus(id, 'on_hold', user.id, reason);
    if (ok) { setToast({ msg: 'Project put on hold', type: 'success' }); setSelected(null); }
  };

  const handleCancel = async (id: string, reason: string) => {
    if (!user) return;
    const ok = await updateStatus(id, 'cancelled', user.id, reason);
    if (ok) { setToast({ msg: 'Project cancelled', type: 'success' }); setSelected(null); }
  };

  if (isLensActive && !features.showProjects) {
    return (
      <div>
        <div style={{ fontSize: 24, fontWeight: 700, color: admin.header, letterSpacing: '-0.5px', marginBottom: 24 }}>Projects</div>
        <div style={{ padding: '60px 20px', textAlign: 'center', background: palette.white, borderRadius: admin.cardRadius, border: `1px solid ${admin.border}` }}>
          <EyeOff size={24} color={palette.g400} style={{ marginBottom: 12 }} />
          <div style={{ fontSize: 15, fontWeight: 600, color: palette.g700, marginBottom: 6 }}>Projects is not typically used by {industryName} businesses</div>
          <div style={{ fontSize: 13.5, color: palette.g500 }}>Switch your industry lens to see this module, or exit the lens to return to your view.</div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div>
        <Skeleton width={100} height={24} style={{ marginBottom: 24 }} />
        <div style={{ background: palette.white, borderRadius: admin.cardRadius, border: `1px solid ${admin.border}`, padding: 20 }}><TableSkeleton rows={5} columns={6} /></div>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <div style={{ fontSize: 24, fontWeight: 700, color: admin.header, letterSpacing: '-0.5px', marginBottom: 24 }}>Projects</div>
        <div style={{ padding: '40px 0', textAlign: 'center', color: palette.red, fontSize: 13.5 }}>{error}</div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 24, fontWeight: 700, color: admin.header, letterSpacing: '-0.5px' }}>Projects</div>
          <div style={{ fontSize: 13.5, color: admin.caption, marginTop: 4 }}>Deal-to-delivery project management</div>
        </div>
        <button onClick={() => setShowCreate(true)} style={{
          display: 'flex', alignItems: 'center', gap: 4, padding: '7px 14px', borderRadius: 8,
          border: 'none', background: palette.accent, fontSize: 13, fontWeight: 600,
          color: palette.white, cursor: 'pointer', fontFamily: 'inherit',
        }}><Plus size={15} /> New Project</button>
      </div>

      <div style={{ display: 'flex', gap: 0, background: palette.g100, borderRadius: 8, padding: 2, marginBottom: 16, width: 'fit-content' }}>
        {PROJECT_TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} style={{
            padding: '6px 16px', borderRadius: 5, border: 'none', cursor: 'pointer',
            fontSize: 12.5, fontWeight: 500, fontFamily: 'inherit',
            background: tab === t.key ? palette.g800 : 'transparent',
            color: tab === t.key ? palette.white : palette.g500,
            transition: `all ${transition.fast}`,
          }}>{t.label}</button>
        ))}
      </div>

      <div style={{ background: palette.white, borderRadius: admin.cardRadius, border: `1px solid ${admin.border}`, padding: 20, boxShadow: shadow.card }}>
        {tab === 'active' && (
          <ActiveTab projects={projects} summary={summary} filters={filters} setFilters={setFilters}
            onSelect={setSelected} onAdvance={handleAdvance} onNewProject={() => setShowCreate(true)} />
        )}
        {tab === 'completed' && <CompletedTab projects={allProjects} onSelect={setSelected} />}
      </div>

      <CreateProjectPanel isOpen={showCreate} onClose={() => setShowCreate(false)}
        onCreate={async (data, phases, uid) => { const id = await createProject(data, phases, uid); if (id) setToast({ msg: 'Project created', type: 'success' }); return id; }}
        users={users} userId={user?.id ?? ''} />

      <ProjectDetailPanel project={selected} businessId={business?.id ?? ''} userId={user?.id ?? ''}
        users={users} onClose={() => setSelected(null)} onAdvance={handleAdvance}
        onHold={handleHold} onCancel={handleCancel} />

      {toast && <Toast message={toast.msg} type={toast.type} isVisible={!!toast} onDismiss={() => setToast(null)} />}
    </div>
  );
}
